import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { combineLatest, fromEvent, Subject } from 'rxjs';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';

import { ScrollDirection } from '../../models/scroll-direction.enum';
import { ScrollOrientation } from '../../models/scroll-orientation.enum';

const initialScrollState = {
  hasHorizontalScrollBar: false,
  hasVerticalScrollBar: false,
  scrolledToLeft: true,
  scrolledToRight: false,
  scrolledToTop: true,
  scrolledToBottom: false,
};

const scrollBehavior = 'smooth';

@Component({
  selector: 'atx-scroll-buttons',
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './scroll-buttons.component.html',
  styleUrls: ['./scroll-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScrollButtonsComponent implements AfterViewInit, OnDestroy {
  public readonly ScrollDirection = ScrollDirection;

  @Input() public scrollStep = 250;
  @Input() public offsetHeightMargin = 0;
  @Input() public offsetWidthMargin = 0;
  @Input() public scrollOrientation: ScrollOrientation =
    ScrollOrientation.horizontal;
  @Input() public enableScrollButtons = true;

  @ContentChild('left')
  public customLeftButton?: TemplateRef<unknown>;
  @ContentChild('right')
  public customRightButton?: TemplateRef<unknown>;
  @ContentChild('top')
  public customTopButton?: TemplateRef<unknown>;
  @ContentChild('bottom')
  public customBottomButton?: TemplateRef<unknown>;

  public scrollableContainer?: HTMLElement | null;

  public get isVertical() {
    return this.scrollOrientation === ScrollOrientation.vertical;
  }

  public get isHorizontal() {
    return this.scrollOrientation === ScrollOrientation.horizontal;
  }

  @ViewChild('contentContainer')
  protected contentContainer!: ElementRef<HTMLDivElement>;

  private cdRef = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();
  private resize$ = new Subject<void>();
  private mutation$ = new Subject<void>();

  private resizeObserver: ResizeObserver = new ResizeObserver(() =>
    this.resize$.next(),
  );
  private mutationObserver: MutationObserver = new MutationObserver(() =>
    this.mutation$.next(),
  );

  protected scrollState = initialScrollState;

  protected readonly ScrollOrientation = ScrollOrientation;

  public ngAfterViewInit(): void {
    this.scrollableContainer = this.contentContainer.nativeElement
      .firstChild as HTMLElement;

    if (this.scrollableContainer) {
      this.updateScrollState(this.scrollableContainer);

      this.resizeObserver.observe(this.scrollableContainer);

      // check if container element has added child, especially for lazy loading
      this.mutationObserver.observe(this.scrollableContainer, {
        childList: true,
        subtree: true,
      });

      combineLatest([
        this.resize$.pipe(startWith(void 0)),
        this.mutation$.pipe(startWith(void 0)),
        fromEvent(this.scrollableContainer, 'scroll').pipe(startWith('')),
      ])
        .pipe(debounceTime(50), takeUntil(this.destroy$))
        .subscribe(() =>
          this.updateScrollState(this.scrollableContainer as HTMLElement),
        );
    }
  }

  public ngOnDestroy(): void {
    this.resizeObserver.disconnect();
    this.mutationObserver.disconnect();

    this.destroy$.next();
    this.destroy$.complete();
  }

  public updateScrollState(element: HTMLElement) {
    if (element) {
      const {
        scrollLeft,
        scrollWidth,
        offsetWidth,
        scrollTop,
        scrollHeight,
        offsetHeight,
      } = element;
      this.scrollState = {
        ...this.scrollState,
        hasHorizontalScrollBar:
          scrollWidth > offsetWidth + this.offsetWidthMargin,
        hasVerticalScrollBar:
          scrollHeight > offsetHeight + this.offsetHeightMargin,
        scrolledToLeft: scrollLeft === 0,
        scrolledToRight:
          scrollWidth === scrollLeft + offsetWidth + this.offsetWidthMargin ||
          scrollLeft + offsetWidth + this.offsetWidthMargin > scrollWidth,
        scrolledToTop: scrollTop === 0,
        scrolledToBottom:
          scrollHeight === scrollTop + offsetHeight + this.offsetHeightMargin ||
          scrollTop + offsetHeight + this.offsetHeightMargin > scrollHeight,
      };
    } else {
      this.scrollState = initialScrollState;
    }

    this.cdRef.markForCheck();
  }

  public scroll(direction: ScrollDirection) {
    if (!this.scrollableContainer) {
      return;
    }

    const scrollOptions: ScrollToOptions = {
      behavior: scrollBehavior,
    };

    switch (direction) {
      case ScrollDirection.Right:
        scrollOptions.left =
          this.scrollableContainer.scrollLeft + this.scrollStep;
        break;
      case ScrollDirection.Left:
        scrollOptions.left =
          this.scrollableContainer.scrollLeft - this.scrollStep;
        break;
      case ScrollDirection.Bottom:
        scrollOptions.top =
          this.scrollableContainer.scrollTop + this.scrollStep;
        break;
      case ScrollDirection.Top:
        scrollOptions.top =
          this.scrollableContainer.scrollTop - this.scrollStep;
        break;
    }

    this.scrollableContainer.scrollTo(scrollOptions);
  }
}
